import { Grid } from "@mui/material";
import { useEffect, useState } from "react";
import ChartByTime from "../../components/Chart/ChartByTime";
import ChartByString from "../../components/Chart/ChartByString";
import StatsService from "../../services/statsService";
import {
  CollectionSizeElement,
  ShirtsCountryCountElement,
  ShirtsDecadeCountElement,
  ShirtsNameCountElement,
  ShirtsSeasonCountElement,
} from "../../services/shirts/types";
import StatBox from "./StatBox";

interface NumericalStatsProps {
  userId: number;
}

const NumericalStats = ({ userId }: NumericalStatsProps) => {
  const [collectionSizeStats, setCollectionSizeStats] = useState<
    Array<CollectionSizeElement>
  >([]);
  const [shirtsPerTeamStats, setShirtsPerTeamStats] = useState<
    Array<ShirtsNameCountElement>
  >([]);
  const [shirtsPerCompetitionStats, setShirtsPerCompetitionStats] = useState<
    Array<ShirtsNameCountElement>
  >([]);
  const [shirtsPerCountryStats, setShirtsPerCountryStats] = useState<
    Array<ShirtsCountryCountElement>
  >([]);
  const [shirtsPerTypeStats, setShirtsPerTypeStats] = useState<
    Array<ShirtsNameCountElement>
  >([]);
  const [shirtsPerSeasonStats, setShirtsPerSeasonStats] = useState<
    Array<ShirtsSeasonCountElement>
  >([]);
  const [shirtsPerDecadeStats, setShirtsPerDecadeStats] = useState<
    Array<ShirtsDecadeCountElement>
  >([]);
  const [topTeam, setTopTeam] = useState<string>("none");
  const [currentAmount, setCurrentAmount] = useState<number>(0);
  const [matchwornAmount, setMatchwornAmount] = useState<number>(0);

  const ensureCurrentYear = (
    shirtData: CollectionSizeElement[],
  ): CollectionSizeElement[] => {
    if (shirtData.length === 0) {
      // Si el array está vacío, no hay nada que hacer
      return [];
    }

    const currentYear = new Date().getFullYear();
    const lastShirt = shirtData[shirtData.length - 1];

    if (lastShirt.year === currentYear.toString()) {
      // El último elemento ya pertenece al año actual, no hacemos nada
      return shirtData;
    } else {
      // Agregamos una copia del último elemento con el año actual
      const updatedLastShirt: CollectionSizeElement = {
        year: currentYear.toString(),
        count: lastShirt.count,
      };

      return [...shirtData, updatedLastShirt];
    }
  };

  const {
    getCollectionSizeStats,
    getShirtsPerTeamStats,
    getShirtsPerCompetitionStats,
    getShirtsPerCountryStats,
    getShirtsPerTypeStats,
    getShirtsPerSeasonStats,
    getShirtsPerDecadeStats,
    getShirtsWornInMatchCount,
  } = StatsService();

  useEffect(() => {
    if (userId) {
      getCollectionSizeStats(userId).then((result) => {
        if (result) {
          setCollectionSizeStats(ensureCurrentYear(result));
        }
      });
      getShirtsPerTeamStats(userId).then((result) => {
        if (result) {
          setShirtsPerTeamStats(result);
        }
      });
      getShirtsPerCompetitionStats(userId).then((result) => {
        if (result) {
          setShirtsPerCompetitionStats(result);
        }
      });
      getShirtsPerCountryStats(userId).then((result) => {
        if (result) {
          setShirtsPerCountryStats(result);
        }
      });
      getShirtsPerTypeStats(userId).then((result) => {
        if (result) {
          setShirtsPerTypeStats(result);
        }
      });
      getShirtsPerSeasonStats(userId).then((result) => {
        if (result) {
          setShirtsPerSeasonStats(result);
        }
      });
      getShirtsPerDecadeStats(userId).then((result) => {
        if (result) {
          setShirtsPerDecadeStats(result);
        }
      });
      getShirtsWornInMatchCount().then((result) => {
        if (result) {
          setMatchwornAmount(result);
        }
      });
    }
  }, [userId]);

  useEffect(() => {
    const teamName = shirtsPerTeamStats.reduce(
      (max, shirt) => (shirt.count > max.count ? shirt : max),
      { count: Number.NEGATIVE_INFINITY, name: "none" },
    ).name;
    setTopTeam(teamName);
  }, [shirtsPerTeamStats]);

  useEffect(() => {
    if (collectionSizeStats.length > 0) {
      const amount = collectionSizeStats[collectionSizeStats.length - 1].count;
      setCurrentAmount(amount);
    }
  }, [collectionSizeStats]);

  const blockStyle = {
    width: "100%",
    paddingBottom: "20px",
    paddingTop: "20px",
    borderRadius: "10px",
    backgroundColor: "white !important",
    minHeight: "50px",
    paddingLeft: "16px",
    paddingRight: "16px",
  };

  return (
    <Grid container item direction="column" marginTop={"18px"}>
      <Grid
        item
        container
        xs={12}
        columnSpacing={1}
        rowSpacing={{ md: 0, xs: 1 }}
      >
        <Grid item container xs={12} md={4}>
          <StatBox title="Shirts in collection" value={currentAmount} />
        </Grid>
        <Grid item container xs={12} md={4}>
          <StatBox title="Biggest club in collection" value={topTeam} />
        </Grid>
        <Grid item container xs={12} md={4}>
          <StatBox title="Number of matchworn shirts" value={matchwornAmount} />
        </Grid>
      </Grid>
      <Grid
        item
        container
        xs={12}
        marginTop={"18px"}
        sx={blockStyle}
        justifyContent={"center"}
        alignItems={"center"}
      >
        {/* Number of shirts in collection over time */}
        <ChartByTime
          xData={collectionSizeStats.map((element) => {
            return new Date(parseInt(element.year), 1, 1);
          })}
          yData={collectionSizeStats.map((element) => {
            return element.count;
          })}
          title="Number of shirts in collection over time"
          withMonth={false}
          valueFormatter={(value: number) => {
            return value + " shirts";
          }}
        />
      </Grid>
      <Grid
        item
        container
        xs={12}
        marginTop={"18px"}
        sx={blockStyle}
        justifyContent={"center"}
        alignItems={"center"}
      >
        {/* Current number of shirts per club */}
        <ChartByString
          xData={shirtsPerTeamStats.map((element) => {
            return element.name;
          })}
          yData={shirtsPerTeamStats.map((element) => {
            return element.count;
          })}
          title="Current number of shirts per club"
          valueFormatter={(value: number) => {
            return value + " shirts";
          }}
          bars={true}
        />
      </Grid>
      <Grid
        item
        container
        xs={12}
        marginTop={"18px"}
        sx={blockStyle}
        justifyContent={"center"}
        alignItems={"center"}
      >
        {/* Current number of shirts per competition */}
        <ChartByString
          xData={shirtsPerCompetitionStats.map((element) => {
            return element.name;
          })}
          yData={shirtsPerCompetitionStats.map((element) => {
            return element.count;
          })}
          title="Current number of shirts per competition"
          valueFormatter={(value: number) => {
            return value + " shirts";
          }}
          bars={true}
        />
      </Grid>
      <Grid
        item
        container
        xs={12}
        marginTop={"18px"}
        sx={blockStyle}
        justifyContent={"center"}
        alignItems={"center"}
      >
        {/* Current number of shirts per country */}
        <ChartByString
          xData={shirtsPerCountryStats.map((element) => {
            return element.country;
          })}
          yData={shirtsPerCountryStats.map((element) => {
            return element.count;
          })}
          title="Current number of shirts per country"
          valueFormatter={(value: number) => {
            return value + " shirts";
          }}
          bars={true}
        />
      </Grid>
      <Grid
        item
        container
        xs={12}
        marginTop={"18px"}
        sx={blockStyle}
        justifyContent={"center"}
        alignItems={"center"}
      >
        {/* Current number of shirts per type of shirt */}
        <ChartByString
          xData={shirtsPerTypeStats.map((element) => {
            return element.name;
          })}
          yData={shirtsPerTypeStats.map((element) => {
            return element.count;
          })}
          title="Current number of shirts per type of shirt"
          valueFormatter={(value: number) => {
            return value + " shirts";
          }}
          bars={true}
        />
      </Grid>
      <Grid
        item
        container
        xs={12}
        marginTop={"18px"}
        sx={blockStyle}
        justifyContent={"center"}
        alignItems={"center"}
      >
        {/* Current number of shirts per season */}
        <ChartByTime
          xData={shirtsPerSeasonStats.map((element) => {
            return new Date(element.id, 1, 1);
          })}
          yData={shirtsPerSeasonStats.map((element) => {
            return element.count;
          })}
          title="Current number of shirts per season"
          valueFormatter={(value: number) => {
            return value + " shirts";
          }}
          withMonth={false}
          bars={true}
        />
      </Grid>
      <Grid
        item
        container
        xs={12}
        marginTop={"18px"}
        sx={blockStyle}
        justifyContent={"center"}
        alignItems={"center"}
      >
        {/* Current number of shirts per decade */}
        <ChartByTime
          xData={shirtsPerDecadeStats.map((element) => {
            return new Date(parseInt(element.decade), 1, 1);
          })}
          yData={shirtsPerDecadeStats.map((element) => {
            return element.count;
          })}
          title="Current number of shirts per decade"
          valueFormatter={(value: number) => {
            return value + " shirts";
          }}
          withMonth={false}
          bars={true}
        />
      </Grid>
    </Grid>
  );
};

export default NumericalStats;
